import { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SportsVolleyballIcon from '@mui/icons-material/SportsVolleyball';
import type { TeamSide } from './quickMatchTypes.ts';

interface QuickMatchSetupProps {
  homeTeamName?: string;
  awayTeamName?: string;
  isNewSet?: boolean;
  onStart: (params: { homeTeamName: string; awayTeamName: string; servingTeam: TeamSide; pointsToWin: number }) => void;
}

const POINTS_OPTIONS = [15, 21, 25];

export default function QuickMatchSetup({ homeTeamName: initHome = 'Noi', awayTeamName: initAway = 'Loro', isNewSet = false, onStart }: QuickMatchSetupProps) {
  const [homeTeamName, setHomeTeamName] = useState(initHome);
  const [awayTeamName, setAwayTeamName] = useState(initAway);
  const [servingTeam, setServingTeam] = useState<TeamSide>('HOME');
  const [pointsToWin, setPointsToWin] = useState(25);

  const handleStart = () => {
    const home = homeTeamName.trim() || 'Noi';
    const away = awayTeamName.trim() || 'Loro';
    onStart({ homeTeamName: home, awayTeamName: away, servingTeam, pointsToWin });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        py: 4,
        px: 3,
        maxWidth: 480,
        mx: 'auto',
      }}
    >
      <SportsVolleyballIcon sx={{ fontSize: 56, color: 'primary.main' }} />

      <Typography variant="h5" fontWeight={700}>
        {isNewSet ? 'Nuovo set' : 'Partita veloce'}
      </Typography>

      {!isNewSet && (
        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <TextField
            label="Squadra A"
            value={homeTeamName}
            onChange={(e) => setHomeTeamName(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 20 }}
          />
          <TextField
            label="Squadra B"
            value={awayTeamName}
            onChange={(e) => setAwayTeamName(e.target.value)}
            fullWidth
            inputProps={{ maxLength: 20 }}
          />
        </Box>
      )}

      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Chi batte per primo?
        </Typography>
        <ToggleButtonGroup
          value={servingTeam}
          exclusive
          onChange={(_, val) => val && setServingTeam(val)}
          fullWidth
        >
          <ToggleButton value="HOME" sx={{ fontWeight: 600 }}>
            🏐 {isNewSet ? initHome : homeTeamName || 'Noi'}
          </ToggleButton>
          <ToggleButton value="AWAY" sx={{ fontWeight: 600 }}>
            {isNewSet ? initAway : awayTeamName || 'Loro'} 🏐
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary" mb={1}>
          Punti per vincere il set
        </Typography>
        <ToggleButtonGroup
          value={POINTS_OPTIONS.includes(pointsToWin) ? pointsToWin : null}
          exclusive
          onChange={(_, val) => val && setPointsToWin(val)}
          fullWidth
          sx={{ mb: 1.5 }}
        >
          {POINTS_OPTIONS.map((p) => (
            <ToggleButton key={p} value={p} sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              {p}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <TextField
          label="Valore personalizzato"
          type="number"
          value={pointsToWin}
          onChange={(e) => {
            const n = parseInt(e.target.value);
            if (!isNaN(n) && n >= 5 && n <= 99) setPointsToWin(n);
          }}
          fullWidth
          size="small"
          inputProps={{ min: 5, max: 99 }}
          helperText="Minimo 5, massimo 99"
        />
      </Box>

      <Button
        variant="contained"
        size="large"
        onClick={handleStart}
        fullWidth
        sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 700 }}
      >
        {isNewSet ? 'Inizia il set' : 'Inizia la partita'}
      </Button>
    </Box>
  );
}
